import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { COMPANY, SERVICE_OPTIONS } from "@/data/site";

const initial = { name: "", company: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState([]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.service) e.service = "Select a service";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Tell us a bit more (min 10 chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please review the highlighted fields.");
      return;
    }
    const payload = { ...form, submittedAt: new Date().toISOString() };
    // MVP: log + local state
    // eslint-disable-next-line no-console
    console.log("[Printzee] Contact submission:", payload);
    setSubmitted((prev) => [payload, ...prev]);
    toast.success("Thanks! We'll get back to you within a few hours.");
    setForm(initial);
    setErrors({});
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#F8FAFC] py-24 md:py-32">
      <div className="pz-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: Info */}
          <div className="lg:col-span-5">
            <span className="pz-overline">Let&apos;s talk</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
              Start your next<br />project with us.
            </h2>
            <div className="pz-gold-line mt-6" />
            <p className="mt-6 text-slate-600">
              Tell us what you&apos;re planning — a launch, a fit-out, a gifting drop, or a complete rebrand. We&apos;ll come back with a tailored proposal.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { icon: Mail, label: "Email us", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
                { icon: Phone, label: "Call us", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
                { icon: MapPin, label: "Visit us", value: COMPANY.address },
                // { icon: Clock, label: "Working hours", value: COMPANY.hours },
              ].map((row) => {
                const Icon = row.icon;
                const inner = (
                  <>
                    <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 group-hover:border-[#0F172A] flex items-center justify-center transition-colors">
                      <Icon className="w-5 h-5 text-[#0F172A]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{row.label}</div>
                      <div className="font-display text-[#0F172A] text-base font-medium mt-1">{row.value}</div>
                    </div>
                  </>
                );
                return row.href ? (
                  <a
                    href={row.href}
                    key={row.label}
                    data-testid={`contact-${row.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="group flex items-center gap-4 hover:translate-x-1 transition-transform"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={row.label} className="group flex items-center gap-4">{inner}</div>
                );
              })}
            </div>

            {submitted.length > 0 && (
              <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="contact-submissions-log">
                <div className="flex items-center gap-2 text-emerald-700 font-medium">
                  <CheckCircle2 className="w-5 h-5" /> {submitted.length} submission{submitted.length > 1 ? "s" : ""} captured this session
                </div>
                <p className="text-sm text-emerald-700/80 mt-1">Logged to console for review (MVP mode).</p>
              </div>
            )}
          </div>

          {/* Right: Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white rounded-3xl p-7 sm:p-10 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.18)] border border-slate-100"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Your Name" error={errors.name}>
                <input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Cooper"
                  className={inputCls(errors.name)}
                />
              </Field>
              <Field label="Company">
                <input
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Acme Holdings"
                  className={inputCls()}
                />
              </Field>
              <Field label="Email" error={errors.email}>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  className={inputCls(errors.email)}
                />
              </Field>
              <Field label="Phone" error={errors.phone}>
                <input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+971 50 000 0000"
                  className={inputCls(errors.phone)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Service Required" error={errors.service}>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger
                      data-testid="contact-select-service"
                      className={`h-12 rounded-xl border ${errors.service ? "border-red-400" : "border-slate-200"} bg-white text-left`}
                    >
                      <SelectValue placeholder="Pick the service you need" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`contact-service-option-${s.slice(0, 8)}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Project Details" error={errors.message}>
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your project, timelines and any inspiration links..."
                    rows={5}
                    className={`${inputCls(errors.message)} resize-none py-3`}
                  />
                </Field>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-slate-500">
                By submitting, you agree to be contacted about your project. We respect your privacy.
              </p>
              <button
                type="submit"
                data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full pz-btn-gold text-sm"
              >
                Send Enquiry <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">{label}</label>
      {children}
      {error && <div className="mt-1.5 text-xs text-red-500">{error}</div>}
    </div>
  );
}

function inputCls(error) {
  return `w-full h-12 rounded-xl border ${error ? "border-red-400" : "border-slate-200"} bg-white px-4 text-[15px] text-[#0F172A] placeholder:text-slate-400 outline-none focus:border-[#0F172A] focus:ring-2 focus:ring-[#2563EB]/20 transition-all`;
}
